/**
 * The files a step carries: each one as a chip with its picture, name and size, and the one button that adds
 * more through the shared Assets control. Files dropped on the field go into the control, which uploads them and
 * picks the finished one.
 */
import { useRef, useState } from "react";
import type { Attachment, LibraryAsset } from "../../shared/types";
import { openAssets } from "../assets/AssetsPicker";
import { bytesLabel, type Fit, FITS, lenLabel } from "../assets/library";
import { noteMediaKey, notePosterKey } from "../lib/noteUpload";
import { Icon } from "./ui/Icon";

/** A file a step can carry: anything in Assets but a room's replay, which is the room itself. */
const STEP_FILE: Fit = {
  label: "",
  hint: "",
  accept: "",
  at: "all",
  test: (a) => a.source !== "room",
  why: () => "It’s already a room’s replay.",
};

/** What a field takes: only pictures, or any file. */
type FilesFit = "image" | "any";
const FIT: Record<FilesFit, Fit> = { image: FITS.image, any: STEP_FILE };

/** The attachment a step keeps for a file picked in Assets: its stored key, or the note a video or sound plays from. */
export function attachmentOf(a: LibraryAsset): Attachment | null {
  const size = a.size_bytes ? bytesLabel(a.size_bytes) : undefined;
  const base = { name: a.name, ...(size ? { size } : {}) };
  if ((a.kind === "video" || a.kind === "sound") && a.note_id) {
    const kind = a.kind === "video" ? "video" : "voice";
    return {
      ...base,
      kind,
      media_key: noteMediaKey(a.note_id, kind),
      ...(kind === "video" ? { poster_key: notePosterKey(a.note_id) } : {}),
      ...(a.duration_sec ? { duration: lenLabel(a.duration_sec) } : {}),
      ...(a.poster_url ? { thumb: a.poster_url } : {}),
    };
  }
  if (!a.key) return null;
  return { ...base, kind: a.kind === "image" ? "image" : "file", key: a.key };
}

/** The picture a chip shows: the file itself for a picture, its poster for a video, else nothing. */
function thumbOf(a: Attachment): string | null {
  if (a.kind === "image" && a.key) return `/api/assets/${a.key}`;
  return a.thumb ?? null;
}

/** The small line under a file's name. */
function fileSub(a: Attachment): string {
  const kind = a.kind === "image" ? "Picture" : a.kind === "video" ? "Video" : a.kind === "voice" ? "Voice note" : "File";
  return [kind, a.duration, a.size].filter(Boolean).join(" · ");
}

function FileThumb({ att }: { att: Attachment }) {
  const src = thumbOf(att);
  return (
    <span className="fl-athumb">
      {src ? <img src={src} alt="" /> : <Icon name={att.kind === "video" ? "video" : att.kind === "voice" ? "mic" : "file"} />}
    </span>
  );
}

export function FlowFiles({
  label,
  value,
  onChange,
  onToast,
  fits = "any",
}: {
  label: string;
  value: Attachment[];
  onChange: (next: Attachment[]) => void;
  onToast: (message: string) => void;
  fits?: FilesFit;
}) {
  const latest = useRef(value);
  latest.current = value;
  const [over, setOver] = useState(false);

  const open = (incoming?: File[]) =>
    openAssets({
      fits: FIT[fits],
      title: label,
      ...(incoming?.length ? { incoming } : {}),
      onPick: (asset) => {
        const att = attachmentOf(asset);
        if (att) onChange([...latest.current, att]);
        else onToast("That file can’t go on this step.");
      },
    });

  return (
    <div
      className={over ? "fe-fld drop" : "fe-fld"}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        const files = Array.from(e.dataTransfer.files);
        if (!files.length) return;
        e.preventDefault();
        setOver(false);
        open(files);
      }}
    >
      <span>{label}</span>
      <div className="fe-files">
        {value.map((a, i) => (
          <span className="fe-file" key={`${a.key ?? a.media_key ?? a.name}-${i}`}>
            <FileThumb att={a} />
            <span>
              {a.name}
              <em>{fileSub(a)}</em>
            </span>
            <button
              type="button"
              className="ibtn sm"
              aria-label={`Take ${a.name} off`}
              title={`Take ${a.name} off`}
              onClick={() => onChange(value.filter((_x, j) => j !== i))}
            >
              <Icon name="x" />
            </button>
          </span>
        ))}
        <button type="button" className="btn s" onClick={() => open()}>
          <Icon name="fold" />
          From Assets, or upload it
        </button>
      </div>
    </div>
  );
}
